<template>
  <v-select
    ref="hsCodeSelect"
    :id="id"
    v-model="selectedHSCode"
    :options="hsCodeOptions"
    :class="error ? 'is-invalid' : ''"
    placeholder="Select HS Code"
    :loading="loadingHSCode"
    :clearable="true"
    :filterable="false"
    :searchable="true"
    @search="onSearch"
    @input="handleInput"
    @search:blur="onSearchBlur"
  >
    <template #no-options>
      <div class="text-center text-muted p-2">
        <div v-if="loadingHSCode">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <span class="ml-2">Searching...</span>
        </div>
        <div v-else-if="searchTerm">No HS codes found for "{{ searchTerm }}"</div>
        <div v-else>No HS codes available</div>
      </div>
    </template>

    <template #spinner="{ loading }">
      <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </template>

    <template #selected-option="{ code, name }">
      <b-row>
        <b-col md="12" class="text-truncate">
          <strong class="mr-1">{{ code }}</strong>
          <span class="text-truncate">{{ name }}</span>
        </b-col>
      </b-row>
    </template>

    <template #option="{ code, name, description }">
      <b-row>
        <b-col md="12">
          <b-row>
            <b-col md="12" class="text-truncate">
              <strong class="mr-1">{{ code }}</strong>
              <span class="text-truncate">{{ name }}</span>
            </b-col>
          </b-row>

          <b-row v-if="description">
            <b-col md="12" class="text-truncate">
              <small class="text-muted text-truncate d-block">{{ description }}</small>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </template>
  </v-select>
</template>

<script>
import { APIService } from '@/utils/api';
import { RESOURCES } from '@/utils/constants';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { BCol, BRow } from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';

export default {
  name: 'HSCodeDropdown',
  components: {
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
    ValidationObserver,
  },
  props: {
    id: {
      type: String,
      default: () => `hs-code-${Math.random().toString(36).substr(2, 9)}`,
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    error: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'HS Code',
    },
    placeholder: {
      type: String,
      default: 'Search HS codes...',
    },
    params: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      searchTerm: '',
      loadingHSCode: true,
      hsCodeOptions: [],
      selectedHSCode: null,

      // Add debounce timer for search
      searchTimeout: null,

      // Track if we should preserve search term
      isSearching: false,
      lastSearchTerm: '',
    };
  },
  computed: {
    userID() {
      return this.$store.state.user?.id || 1;
    },
    businessID() {
      return this.$store.state.user?.businessID || 1;
    },
  },
  watch: {
    value: {
      handler(newVal) {
        // Handle both object and primitive values
        if (typeof newVal === 'object' && newVal !== null) {
          this.selectedHSCode = newVal;
        } else if (newVal) {
          // If it's a primitive value (ID), find the corresponding object
          const hsCode = this.hsCodeOptions.find((option) => option.id == newVal);
          this.selectedHSCode = hsCode || null;
        } else {
          this.selectedHSCode = null;
        }
      },
      immediate: true,
    },
    hsCodeOptions: {
      handler(newVal) {
        console.log('hsCodeOptions changed:', newVal.length, 'items');
        if (newVal.length > 0) {
          console.log('First HS code option:', newVal[0]);
        }

        // Re-set the selected value if we have a value prop
        if (this.value && typeof this.value !== 'object') {
          const hsCode = newVal.find((option) => option.id == this.value);
          if (hsCode) {
            this.selectedHSCode = hsCode;
          }
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchHSCodes();
  },
  methods: {
    async fetchHSCodes() {
      this.loadingHSCode = true;

      console.log('fetchHSCodes called with searchTerm:', this.searchTerm);

      const params = {
        pageSize: 9999, // Get all results
        ...this.params,
      };

      // Only add searchTerm if it's not empty
      if (this.searchTerm && this.searchTerm.trim()) {
        params.searchTerm = this.searchTerm.trim();
        console.log('Adding searchTerm to params:', params.searchTerm);
      }

      console.log('Final API params:', params);

      try {
        const response = await APIService.getByPagination(
          RESOURCES.HS_CODE,
          this.businessID,
          params
        );

        console.log('HS Codes API Response:', response);

        // Handle different possible response structures
        if (response?.data?.status === 'success') {
          // Check for items in data.data.items (standard pagination)
          if (response.data.data?.items && Array.isArray(response.data.data.items)) {
            console.log('Found items in data.data.items:', response.data.data.items.length);
            this.hsCodeOptions = response.data.data.items;
          }
          // Check for items in data.data.results (alternative structure)
          else if (response.data.data?.results && Array.isArray(response.data.data.results)) {
            console.log('Found items in data.data.results:', response.data.data.results.length);
            this.hsCodeOptions = response.data.data.results;
          }
          // Check for items directly in data.data (direct array)
          else if (Array.isArray(response.data.data)) {
            console.log('Found items in data.data:', response.data.data.length);
            this.hsCodeOptions = response.data.data;
          }
          // Check for items in data (fallback)
          else if (Array.isArray(response.data)) {
            console.log('Found items in data:', response.data.length);
            this.hsCodeOptions = response.data;
          } else {
            console.warn('Unexpected response structure:', response.data);
            this.hsCodeOptions = [];
          }
        } else {
          console.warn('API returned non-success status:', response?.data?.status);
          this.hsCodeOptions = [];
        }

        console.log('Final HS code options count:', this.hsCodeOptions.length);

        // Debug: Log the first few items to see their structure
        if (this.hsCodeOptions.length > 0) {
          console.log('First HS code item structure:', this.hsCodeOptions[0]);
          console.log('Sample HS code keys:', Object.keys(this.hsCodeOptions[0]));
        }
      } catch (error) {
        console.error('Error fetching HS codes:', error);
        this.hsCodeOptions = [];

        // Show user-friendly error message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Failed to load HS codes. Please try again.',
          },
        });
      } finally {
        this.loadingHSCode = false;
      }
    },

    onSearch(searchTerm) {
      console.log('onSearch called with:', searchTerm);

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Update search term immediately for UI responsiveness
      this.searchTerm = searchTerm || '';
      this.isSearching = true;

      // If search term is empty, fetch all options immediately
      if (!searchTerm || searchTerm.trim() === '') {
        this.searchTerm = '';
        this.isSearching = false;
        this.fetchHSCodes();
        return;
      }

      // Debounce the API call for non-empty search terms
      this.searchTimeout = setTimeout(() => {
        this.lastSearchTerm = this.searchTerm;
        this.fetchHSCodes();
        this.isSearching = false;
      }, 300); // 300ms debounce
    },

    handleInput(value) {
      // Emit the ID if an object is selected, or the value directly
      const emitValue = value && typeof value === 'object' ? value.id : value;
      this.$emit('input', emitValue);
    },

    onSearchBlur() {
      // Don't clear search term on blur if we're still searching or have results
      console.log('onSearchBlur called, current searchTerm:', this.searchTerm);

      // Only clear if no options are found and search was empty
      if (!this.searchTerm && this.hsCodeOptions.length === 0) {
        this.fetchHSCodes();
      }
    },
  },
  beforeDestroy() {
    // Clean up timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  ::v-deep {
    .vs__dropdown-option {
      padding: 8px 12px;

      &--highlight {
        background: #f8f9fa;
        color: #2c3e50;
      }
    }

    .vs__dropdown-menu {
      max-height: 350px;
    }

    .text-truncate {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
